<template>
  <Teleport to="body">
    <transition name="cmdk-fade">
      <div v-if="open" class="cmdk-overlay" @click.self="close">
        <div class="cmdk-card" role="dialog" aria-modal="true" aria-label="Command palette">
          <div class="cmdk-search">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="M16.5 16.5 L21 21" />
            </svg>
            <input
              ref="inputRef"
              v-model="query"
              type="text"
              placeholder="Search sections…"
              class="cmdk-input"
              @keydown.down.prevent="moveDown"
              @keydown.up.prevent="moveUp"
              @keydown.enter.prevent="activateSelected"
            />
            <span class="cmdk-kbd">esc</span>
          </div>

          <div v-if="filtered.length === 0" class="cmdk-empty">
            <p>No matches for "{{ query }}".</p>
            <p class="cmdk-empty-hint">Tip: search by section name (Dashboard, Library, Analytics, Billing, Account).</p>
          </div>

          <ul v-else class="cmdk-list">
            <li v-for="(item, i) in filtered" :key="item.id">
              <button
                type="button"
                class="cmdk-item"
                :class="{ 'cmdk-item--selected': i === selectedIndex }"
                @click="activate(item)"
                @mouseenter="selectedIndex = i"
              >
                <span class="cmdk-item-icon" aria-hidden="true">
                  <component :is="item.icon" />
                </span>
                <span class="cmdk-item-text">
                  <span class="cmdk-item-title">{{ item.title }}</span>
                  <span v-if="item.subtitle" class="cmdk-item-subtitle">{{ item.subtitle }}</span>
                </span>
                <span class="cmdk-item-kind">{{ item.kind }}</span>
              </button>
            </li>
          </ul>

          <div class="cmdk-footer">
            <span class="cmdk-footer-hint"><span class="cmdk-kbd">↑↓</span> Navigate</span>
            <span class="cmdk-footer-hint"><span class="cmdk-kbd">↵</span> Open</span>
            <span class="cmdk-footer-hint"><span class="cmdk-kbd">esc</span> Close</span>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script lang="ts">
import { computed, defineComponent, h, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

export interface PaletteItem {
  id: string
  title: string
  subtitle?: string
  kind: string
  path: string
  icon: ReturnType<typeof h>
  keywords?: string[]
}

// Inline SVG factories (line-style, matches rail icon language)
const dashIcon = () => h('svg', { viewBox: '0 0 24 24', width: 18, height: 18, fill: 'none', stroke: 'currentColor', 'stroke-width': 1.7, 'stroke-linejoin': 'round' }, [
  h('rect', { x: 3, y: 3, width: 8, height: 10, rx: 1.6 }),
  h('rect', { x: 13, y: 3, width: 8, height: 6, rx: 1.6 }),
  h('rect', { x: 13, y: 11, width: 8, height: 10, rx: 1.6 }),
  h('rect', { x: 3, y: 15, width: 8, height: 6, rx: 1.6 }),
])
const libraryIcon = () => h('svg', { viewBox: '0 0 24 24', width: 18, height: 18, fill: 'none', stroke: 'currentColor', 'stroke-width': 1.7, 'stroke-linejoin': 'round' }, [
  h('rect', { x: 6, y: 3, width: 14, height: 14, rx: 2.2 }),
  h('rect', { x: 3, y: 6, width: 14, height: 14, rx: 2.2 }),
])
const analyticsIcon = () => h('svg', { viewBox: '0 0 24 24', width: 18, height: 18, fill: 'none', stroke: 'currentColor', 'stroke-width': 1.7, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, [
  h('path', { d: 'M5 19 L5 13' }),
  h('path', { d: 'M10 19 L10 7' }),
  h('path', { d: 'M15 19 L15 14' }),
  h('path', { d: 'M20 19 L20 4' }),
  h('path', { d: 'M3 21 L21 21' }),
])
const billingIcon = () => h('svg', { viewBox: '0 0 24 24', width: 18, height: 18, fill: 'none', stroke: 'currentColor', 'stroke-width': 1.7, 'stroke-linejoin': 'round' }, [
  h('rect', { x: 3, y: 6, width: 18, height: 13, rx: 2 }),
  h('path', { d: 'M3 10 L21 10' }),
  h('path', { d: 'M7 15 L11 15' }),
])
const accountIcon = () => h('svg', { viewBox: '0 0 24 24', width: 18, height: 18, fill: 'none', stroke: 'currentColor', 'stroke-width': 1.7, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, [
  h('circle', { cx: 12, cy: 8, r: 3.6 }),
  h('path', { d: 'M5 20 C5 15.5 8.2 13.5 12 13.5 C15.8 13.5 19 15.5 19 20' }),
])
const newCampaignIcon = () => h('svg', { viewBox: '0 0 24 24', width: 18, height: 18, fill: 'none', stroke: 'currentColor', 'stroke-width': 2.2, 'stroke-linecap': 'round' }, [
  h('path', { d: 'M12 5 L12 19' }),
  h('path', { d: 'M5 12 L19 12' }),
])
const newExperienceIcon = () => h('svg', { viewBox: '0 0 24 24', width: 18, height: 18, fill: 'none', stroke: 'currentColor', 'stroke-width': 1.7, 'stroke-linejoin': 'round', 'stroke-linecap': 'round' }, [
  h('path', { d: 'M12 3 L13.4 9.2 L19.6 10.6 L13.4 12 L12 18.2 L10.6 12 L4.4 10.6 L10.6 9.2 Z' }),
])

const ITEMS: PaletteItem[] = [
  { id: 'go-dashboard', title: 'Dashboard', subtitle: 'Manage your campaigns', kind: 'Section', path: '/dashboard', icon: dashIcon(), keywords: ['campaigns', 'home', 'manage'] },
  { id: 'go-library', title: 'Experience Library', subtitle: 'Reusable quizzes & surveys', kind: 'Section', path: '/dashboard/experiences', icon: libraryIcon(), keywords: ['experiences', 'quiz', 'survey', 'family'] },
  { id: 'go-analytics', title: 'Analytics', subtitle: 'Performance & reports', kind: 'Section', path: '/dashboard/analytics', icon: analyticsIcon(), keywords: ['reports', 'stats', 'metrics', 'performance'] },
  { id: 'go-billing', title: 'Billing', subtitle: 'Plan, tokens, expansions', kind: 'Section', path: '/billing', icon: billingIcon(), keywords: ['plan', 'subscription', 'tokens', 'invoice', 'payment'] },
  { id: 'go-account', title: 'Account', subtitle: 'Personal details & workspaces', kind: 'Section', path: '/profile', icon: accountIcon(), keywords: ['profile', 'org', 'tenant', 'switch', 'memberships'] },
  { id: 'new-campaign', title: 'New campaign', subtitle: 'Start the campaign builder', kind: 'Action', path: '/', icon: newCampaignIcon(), keywords: ['create', 'build', 'start'] },
  { id: 'new-experience', title: 'New experience', subtitle: 'Author a new quiz/survey', kind: 'Action', path: '/dashboard/experiences/new', icon: newExperienceIcon(), keywords: ['create', 'author', 'draft'] },
]

export default defineComponent({
  name: 'CommandPalette',
  props: {
    open: { type: Boolean, default: false },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const router = useRouter()
    const query = ref('')
    const selectedIndex = ref(0)
    const inputRef = ref<HTMLInputElement | null>(null)

    const filtered = computed<PaletteItem[]>(() => {
      const q = query.value.trim().toLowerCase()
      if (!q) return ITEMS
      return ITEMS.filter(it => {
        const hay = [it.title, it.subtitle ?? '', ...(it.keywords ?? [])].join(' ').toLowerCase()
        return hay.includes(q)
      })
    })

    watch(filtered, list => {
      if (selectedIndex.value >= list.length) selectedIndex.value = 0
    })

    function close() {
      emit('close')
    }

    function moveDown() {
      if (filtered.value.length === 0) return
      selectedIndex.value = (selectedIndex.value + 1) % filtered.value.length
    }
    function moveUp() {
      if (filtered.value.length === 0) return
      selectedIndex.value = (selectedIndex.value - 1 + filtered.value.length) % filtered.value.length
    }

    function activate(item: PaletteItem) {
      close()
      router.push(item.path)
    }

    function activateSelected() {
      const it = filtered.value[selectedIndex.value]
      if (it) activate(it)
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && props.open) {
        e.preventDefault()
        close()
      }
    }

    watch(
      () => props.open,
      async isOpen => {
        if (isOpen) {
          query.value = ''
          selectedIndex.value = 0
          await nextTick()
          inputRef.value?.focus()
        }
      }
    )

    onMounted(() => window.addEventListener('keydown', onKey))
    onUnmounted(() => window.removeEventListener('keydown', onKey))

    return { query, selectedIndex, filtered, inputRef, close, moveDown, moveUp, activate, activateSelected }
  },
})
</script>

<style scoped>
.cmdk-overlay {
  position: fixed;
  inset: 0;
  z-index: 400;
  background: rgba(10, 13, 25, 0.55);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 12vh 16px 16px;
  font-family: var(--font-sans);
}

.cmdk-card {
  width: 100%;
  max-width: 560px;
  background: #1a2332;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
  display: flex;
  flex-direction: column;
}

.cmdk-search {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.5);
}

.cmdk-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #ffffff;
  font-size: 15px;
  font-family: var(--font-sans);
  font-weight: 400;
}
.cmdk-input::placeholder { color: rgba(255, 255, 255, 0.4); }

.cmdk-kbd {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
  font-family: var(--font-sans);
  font-weight: 700;
  letter-spacing: 0.04em;
}

.cmdk-empty {
  padding: 32px 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.55);
  font-size: 13px;
  font-weight: 300;
}
.cmdk-empty-hint {
  margin-top: 6px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
}

.cmdk-list {
  list-style: none;
  margin: 0;
  padding: 6px 6px;
  max-height: 50vh;
  overflow-y: auto;
}

.cmdk-item {
  display: flex !important;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px !important;
  border-radius: 8px !important;
  background: transparent !important;
  border: none !important;
  cursor: pointer;
  font-family: var(--font-sans);
  text-align: left;
  color: rgba(255, 255, 255, 0.85);
}

.cmdk-item--selected {
  background: rgba(150, 255, 201, 0.08) !important;
  color: #ffffff;
}

.cmdk-item-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #96ffc9;
  flex-shrink: 0;
}
.cmdk-item--selected .cmdk-item-icon {
  background: rgba(150, 255, 201, 0.16);
}

.cmdk-item-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.cmdk-item-title {
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
}
.cmdk-item-subtitle {
  font-size: 11px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.55);
}

.cmdk-item-kind {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
  padding: 2px 6px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  flex-shrink: 0;
}

.cmdk-footer {
  display: flex;
  gap: 14px;
  padding: 10px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.02);
}
.cmdk-footer-hint {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.45);
  font-weight: 400;
}

.cmdk-fade-enter-active,
.cmdk-fade-leave-active {
  transition: opacity 0.15s ease;
}
.cmdk-fade-enter-active .cmdk-card,
.cmdk-fade-leave-active .cmdk-card {
  transition: transform 0.18s cubic-bezier(0.22, 1, 0.36, 1);
}
.cmdk-fade-enter-from,
.cmdk-fade-leave-to { opacity: 0; }
.cmdk-fade-enter-from .cmdk-card,
.cmdk-fade-leave-to .cmdk-card { transform: translateY(-12px) scale(0.98); }
</style>
